import { useNode } from "@craftjs/core";
import {
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  Button,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import { useEffect, useRef, useState } from "react";
import { ImageUploader } from "./ImageUploader";

interface CFAQItemData {
  question: string;
  answer: string
}

interface CFAQItemProps extends CFAQItemData {
  titleFontSize?: any;
  titleColor?: any;
  titleFontWeight?: any;
  titleAlign?: any;
  titleFontFamily?: any,
  contentFontSize?: any;
  contentColor?: any;
  contentFontWeight?: any;
  contentAlign?: any;
  contentFontFamily?: any;
  borderColor?: any;
  borderWeight?: any;
  expandIcon?: any;
  expandIconWidth?: any;
  expandIconHeight?: any;
}

const CFAQItem = ({
  question, 
  answer,
  titleFontSize,
  titleColor,
  titleFontWeight,
  titleAlign,
  titleFontFamily,
  contentFontSize,
  contentColor,
  contentFontWeight,
  contentAlign,
  contentFontFamily,
  borderColor,
  borderWeight,
  expandIcon,
  expandIconWidth,
  expandIconHeight,
}: CFAQItemProps) => {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<any>(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    if (contentRef.current) {
      setHeight(open ? `${contentRef.current?.scrollHeight ?? 0}px` : '0px');
    }
  }, [open]);

  return (
    <Stack 
      className="craft-faq-item" 
      style={{ 
        padding: "10px 0",
        borderTop: `${borderWeight} solid ${borderColor}`
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h3" style={{
          width: '80%',
          fontSize: titleFontSize,
          fontWeight: titleFontWeight,
          color: titleColor,
          textAlign: titleAlign,
          fontFamily: titleFontFamily,
        }}>
          {question}
        </Typography>
        <Box sx={{
          cursor: "pointer",
          transform: `${open ? 'rotate(45deg)' : 'rotate(0deg)'}`,
          transition: 'transform 1s ease-in-out',
        }}
        onClick={() => setOpen(!open)}
        >
          {
            expandIcon ? (
              <img src={expandIcon} width={expandIconWidth} height={expandIconHeight} />
            ) : (
              <span style={{fontSize: '32px'}}>+</span>
            )
          }
        </Box>
      </Stack>
      <Box sx={{
        height: height,
        overflow: 'hidden',
        transition: 'height 1s ease-in-out',
      }}>
        <Typography ref={contentRef} variant="body1" sx={{ 
          padding: "10px 0 5px",
          fontSize: contentFontSize,
          fontWeight: contentFontWeight,
          color: contentColor,
          textAlign: contentAlign,
          fontFamily: contentFontFamily,
        }}>
          {answer}
        </Typography>
      </Box>
    </Stack>
  )
}


interface CFAQProps {
  faqs?: CFAQItemData[];
  titleFontSize?: any;
  titleColor?: any;
  titleFontWeight?: any;
  titleAlign?: any;
  titleFontFamily?: any,
  contentFontSize?: any;
  contentColor?: any;
  contentFontWeight?: any;
  contentAlign?: any;
  contentFontFamily?: any;
  borderColor?: any;
  borderWeight?: any;
  expandIcon?: any;
  expandIconWidth?: any;
  expandIconHeight?: any;
  [key: string]: any;
}

export const CFAQ = ({
  faqs,
  titleFontSize,
  titleColor,
  titleFontWeight,
  titleAlign,
  titleFontFamily,
  contentFontSize,
  contentColor,
  contentFontWeight,
  contentAlign,
  contentFontFamily,
  borderColor,
  borderWeight,
  expandIcon,
  expandIconWidth,
  expandIconHeight,
  ...props
}: CFAQProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <Stack
      {...props}
      ref={(val: any) => connect(drag(val))}
      className="craft-faq"
      sx={{
        minHeight: '30px',
        borderColor: selected ? "#008522 !important" : "#F0F0F0",
        width: "100%",
        boxSizing: "border-box",
        "& > .craft-faq-item:last-child" : {
          borderBottom: `${borderWeight} solid ${borderColor}`,
        }
      }}
    >
      {faqs?.map((faq, index) => (
        <CFAQItem 
          key={index} 
          question={faq.question} 
          answer={faq.answer} 
          titleFontSize={titleFontSize}
          titleColor={titleColor}
          titleFontWeight={titleFontWeight}
          titleAlign={titleAlign}
          titleFontFamily={titleFontFamily}
          contentFontSize={contentFontSize}
          contentColor={contentColor}
          contentFontWeight={contentFontWeight}
          contentAlign={contentAlign}
          contentFontFamily={contentFontFamily}
          borderColor={borderColor}
          borderWeight={borderWeight}
          expandIcon={expandIcon}
          expandIconWidth={expandIconWidth}
          expandIconHeight={expandIconHeight}
        />
      ))}
    </Stack>
  );
};

export const CFAQSettings = () => {
  const {
    faqs,
    titleFontSize,
    titleColor,
    titleFontWeight,
    titleAlign,
    titleFontFamily,
    contentFontSize,
    contentColor,
    contentFontWeight,
    contentAlign,
    contentFontFamily,
    borderColor,
    borderWeight,
    expandIcon,
    expandIconWidth,
    expandIconHeight,
    actions: { setProp },
  } = useNode((node) => ({
    faqs: node.data.props.faqs,
    titleFontSize: node.data.props.titleFontSize,
    titleColor: node.data.props.titleColor,
    titleFontWeight: node.data.props.titleFontWeight,
    titleAlign: node.data.props.titleAlign,
    titleFontFamily: node.data.props.titleFontFamily,
    contentFontSize: node.data.props.contentFontSize,
    contentColor: node.data.props.contentColor,
    contentFontWeight: node.data.props.contentFontWeight,
    contentAlign: node.data.props.contentAlign,
    contentFontFamily: node.data.props.contentFontFamily,
    borderColor: node.data.props.borderColor,
    borderWeight: node.data.props.borderWeight,
    expandIcon: node.data.props.expandIcon,
    expandIconWidth: node.data.props.expandIconWidth,
    expandIconHeight: node.data.props.expandIconHeight,
  }));

  const handleAddFAQ = () => {
    setProp((props: any) => props.faqs.push({ question: "", answer: "" }));
  };

  const handleRemoveFAQ = (index: number) => {
    setProp((props: any) => props.faqs.splice(index, 1));
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    setProp((props: any) => (props.faqs[index][field] = value));
  };

  return (
    <Stack spacing={2} paddingBottom={4}>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Title Font Size</FormLabel>
          <TextField
            value={titleFontSize}
            onChange={(e) =>
              setProp((props: any) => (props.titleFontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Title Font Family</FormLabel>
          <Select
            value={titleFontFamily}
            onChange={(e) => setProp((props: any) => (props.titleFontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <FormControl fullWidth={true} margin="normal" component="fieldset">
        <FormLabel component="legend">Title Color</FormLabel>
        <MuiColorInput format='hex' value={titleColor} onChange={(color) => {
          setProp((props: any) => (props.titleColor = color), 500);
        }} />
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Title Font Weight</FormLabel>
        <RadioGroup
          defaultValue={titleFontWeight}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.titleFontWeight = e.target.value))
          }
        >
          <FormControlLabel
            label="100"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="200"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="300"
            value="300"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="400"
            value="400"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="500"
            value="500"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="600"
            value="600"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="700"
            value="700"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="800"
            value="800"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="900"
            value="900"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Title Align</FormLabel>
        <RadioGroup
          defaultValue={titleAlign}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.titleAlign = e.target.value))
          }
        >
          <FormControlLabel
            label="Left"
            value="left"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Center"
            value="center"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Right"
            value="right"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Content Font Size</FormLabel>
          <TextField
            value={contentFontSize}
            onChange={(e) =>
              setProp((props: any) => (props.contentFontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Content Font Family</FormLabel>
          <Select
            value={contentFontFamily}
            onChange={(e) => setProp((props: any) => (props.contentFontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Contnet Color</FormLabel>
          <MuiColorInput format='hex' value={contentColor} onChange={(color) => {
            setProp((props: any) => (props.contentColor = color), 500);
          }} />
        </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Content Font Weight</FormLabel>
        <RadioGroup
          defaultValue={contentFontWeight}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.contentFontWeight = e.target.value))
          }
        >
          <FormControlLabel
            label="100"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="200"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="300"
            value="300"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="400"
            value="400"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="500"
            value="500"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="600"
            value="600"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="700"
            value="700"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="800"
            value="800"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="900"
            value="900"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Content Align</FormLabel>
        <RadioGroup
          defaultValue={contentAlign}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.contentAlign = e.target.value))
          }
        >
          <FormControlLabel
            label="Left"
            value="left"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Center"
            value="center"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Right"
            value="right"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Weight</FormLabel>
          <TextField
            value={borderWeight}
            onChange={(e) =>
              setProp((props: any) => (props.borderWeight = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Color</FormLabel>
          <MuiColorInput format='hex' value={borderColor} onChange={(color) => {
            setProp((props: any) => (props.borderColor = color), 500);
          }} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Expand Icon</FormLabel>
          <ImageUploader onImageChange={(url: string) => setProp((props: any) => (props.expandIcon = url), 500)} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Expand Icon Weight</FormLabel>
          <TextField
            value={expandIconWidth}
            onChange={(e) =>
              setProp((props: any) => (props.expandIconWidth = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Expand Icon Height</FormLabel>
          <TextField
            value={expandIconHeight}
            onChange={(e) =>
              setProp((props: any) => (props.expandIconHeight = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack paddingTop={4}>
        <Button variant="contained" color="primary" onClick={handleAddFAQ}>
          Add FAQ
        </Button>
      </Stack>
      {faqs.map((faq: CFAQItemData, index: number) => (
        <Stack key={index} spacing={1} marginBottom={2} p={1} borderBottom="1px solid #F0F0F0">
          <FormControl fullWidth={true} size="small">
            <FormLabel component="legend">Question</FormLabel>
            <TextField
              value={faq.question}
              onChange={(e) =>
                handleInputChange(index, "question", e.target.value)
              }
              variant="outlined"
              size="small"
            />
          </FormControl>
          <FormControl fullWidth={true} size="small" sx={{
            '* > .MuiInputBase-root': {
              height: 'auto !important',
            }
          }}>
            <FormLabel component="legend">Answer</FormLabel>
            <TextField
              value={faq.answer}
              onChange={(e) =>
                handleInputChange(index, "answer", e.target.value)
              }
              style={{
                height: 'auto',
              }}
              variant="outlined"
              size="small"
              multiline
              rows={4}
            />
          </FormControl>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleRemoveFAQ(index)}
          >
            Remove
          </Button>
        </Stack>
      ))}
    </Stack>
  );
};

export const CFAQDefaultProps: CFAQProps = {
  faqs: [],
  titleFontSize: "18px",
  titleColor: "#000000",
  titleFontWeight: "600",
  titleAlign: "left",
  contentFontSize: "15px",
  contentColor: "#000000",
  contentFontWeight: "4000",
  contentAlign: "left",
  borderColor: "#d5d5d5",
  borderWeight: "2px",
  expandIcon: "",
  expandIconWidth: "22px",
  expandIconHeight: "22px",
  // titleFontFamily: "Open Sans,sans-serif",
  // contentFontFamily: "Open Sans,sans-serif",
};

CFAQ.craft = {
  displayName: "FAQ",
  props: CFAQDefaultProps,
  related: {
    settings: CFAQSettings,
  },
};
